import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { Subscription, finalize } from 'rxjs';
import jsPDF from 'jspdf';
import { ReportAnalysisService } from '../../../service/admin/report-analysis.service';
import { ToasterService } from '../../../service/common/toaster.service';

type ComparePhoto = {
  id?: number;
  url?: string;
  name?: string;
  report_date?: string;
  uploaded_at?: string;
};

type PhotoObservation = {
  group?: string;
  label?: string;
  date: string;
  visible_findings: string;
};

type ClinicalTone = 'positive' | 'caution' | 'warning' | 'negative' | 'neutral';

type PhotoProgressMetric = {
  key: string;
  label: string;
  icon: string;
  text: string;
  beforeText: string;
  afterText: string;
  tone: ClinicalTone;
};

type PairedFinding = {
  index: number;
  beforePhoto: ComparePhoto | null;
  afterPhoto: ComparePhoto | null;
  beforeFindings: string;
  afterFindings: string;
  beforeDate: string;
  afterDate: string;
};

type PhotoProgressViewModel = {
  title: string;
  site: string;
  level: string;
  levelTone: ClinicalTone;
  levelIcon: string;
  levelLabel: string;
  intervalComparison: string;
  recommendedCare: string;
  clinicianNotes: string;
  metrics: PhotoProgressMetric[];
  pairs: PairedFinding[];
  unpairedBefore: Array<{ photo: ComparePhoto | null; findings: string; date: string }>;
  unpairedAfter: Array<{ photo: ComparePhoto | null; findings: string; date: string }>;
  caveats: string[];
  metricSummary: { positive: number; caution: number; warning: number; negative: number; neutral: number };
};

@Component({
  selector: 'app-photo-progress-compare-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  templateUrl: './photo-progress-compare-dialog.component.html',
  styleUrl: './photo-progress-compare-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoProgressCompareDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() beforePhotos: ComparePhoto[] = [];
  @Input() afterPhotos: ComparePhoto[] = [];
  @Input() aiEnabled = false;
  @Input() prescriptionId: number | null = null;
  @Input() chiefComplaints = '';
  @Input() diagnoses = '';
  @Input() treatmentContext = '';
  @Input() patientExportLabel = 'Patient';
  @Input() initialAnalysis: any = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() analysisSaved = new EventEmitter<any>();

  loading = false;
  exporting = false;
  error: string | null = null;
  elapsedSeconds = 0;
  analysis: any = null;
  viewModel: PhotoProgressViewModel | null = null;
  activePairIndex = 0;

  zoomScale = 1;
  panX = 0;
  panY = 0;
  isDragging = false;

  /** Clinical panel open by default so doctors see metrics at a glance. */
  reportPanelOpen = true;
  collapsedSections: Record<string, boolean> = {};

  private readonly timeoutMs = 120000;
  private requestSub?: Subscription;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  private cancelled = false;
  private dragLastX = 0;
  private dragLastY = 0;
  private boundPanMove: ((e: MouseEvent) => void) | null = null;
  private boundPanEnd: (() => void) | null = null;

  constructor(
    private readonly reportAnalysisService: ReportAnalysisService,
    private readonly toast: ToasterService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.error = null;
      this.resetZoom();
      this.reportPanelOpen = true;
      if (this.initialAnalysis) {
        this.analysis = this.initialAnalysis;
        this.rebuildViewModel();
        this.cdr.markForCheck();
      } else {
        this.tryLoadExisting();
      }
    }
    if (changes['visible']?.currentValue === false) {
      this.cancelAnalysis();
    }
    if ((changes['initialAnalysis'] || changes['beforePhotos'] || changes['afterPhotos']) && this.visible) {
      if (this.initialAnalysis) {
        this.analysis = this.initialAnalysis;
      }
      if (this.analysis) {
        this.rebuildViewModel();
        this.cdr.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    this.cancelAnalysis();
    this.removePanListeners();
  }

  // ── Getters ────────────────────────────────────────────────────────────

  get canCompare(): boolean {
    return this.beforePhotos.length > 0 && this.afterPhotos.length > 0;
  }

  get activePair(): PairedFinding | null {
    const pairs = this.viewModel?.pairs || [];
    if (!pairs.length) return null;
    const index = Math.min(Math.max(this.activePairIndex, 0), pairs.length - 1);
    return pairs[index] || null;
  }

  get imageTransform(): string {
    return `scale(${this.zoomScale}) translate(${this.panX}px, ${this.panY}px)`;
  }

  get isZoomed(): boolean {
    return this.zoomScale > 1.05;
  }

  get zoomPercent(): number {
    return Math.round(this.zoomScale * 100);
  }

  // ── Pair navigation ────────────────────────────────────────────────────

  selectPair(index: number): void {
    const max = (this.viewModel?.pairs.length || 1) - 1;
    this.activePairIndex = Math.min(Math.max(index, 0), max);
    this.resetZoom();
  }

  prevPair(): void {
    this.selectPair(this.activePairIndex - 1);
  }

  nextPair(): void {
    this.selectPair(this.activePairIndex + 1);
  }

  // ── Synchronized zoom / pan ────────────────────────────────────────────

  onImageWheel(event: WheelEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const factor = event.deltaY > 0 ? 0.88 : 1.14;
    const next = this.zoomScale * factor;
    const clamped = Math.min(Math.max(next, 1), 8);

    if (clamped <= 1.03) {
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
    } else {
      this.zoomScale = Math.round(clamped * 100) / 100;
    }
    this.cdr.markForCheck();
  }

  onPanStart(event: MouseEvent): void {
    if (this.zoomScale <= 1) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragLastX = event.clientX;
    this.dragLastY = event.clientY;

    this.boundPanMove = (e: MouseEvent) => {
      if (!this.isDragging) return;
      const dx = e.clientX - this.dragLastX;
      const dy = e.clientY - this.dragLastY;
      this.panX += dx / this.zoomScale;
      this.panY += dy / this.zoomScale;
      this.dragLastX = e.clientX;
      this.dragLastY = e.clientY;
      this.cdr.markForCheck();
    };
    this.boundPanEnd = () => {
      this.isDragging = false;
      this.removePanListeners();
      this.cdr.markForCheck();
    };

    document.addEventListener('mousemove', this.boundPanMove);
    document.addEventListener('mouseup', this.boundPanEnd);
  }

  resetZoom(): void {
    this.zoomScale = 1;
    this.panX = 0;
    this.panY = 0;
    this.isDragging = false;
    this.removePanListeners();
    this.cdr.markForCheck();
  }

  // ── Report drawer ──────────────────────────────────────────────────────

  toggleReportPanel(): void {
    this.reportPanelOpen = !this.reportPanelOpen;
    this.cdr.markForCheck();
  }

  toggleSection(id: string): void {
    this.collapsedSections[id] = !this.collapsedSections[id];
    this.cdr.markForCheck();
  }

  isSectionOpen(id: string): boolean {
    return !this.collapsedSections[id];
  }

  // ── Dialog actions ─────────────────────────────────────────────────────

  onDialogHide(): void {
    this.cancelAnalysis();
    this.visible = false;
    this.activePairIndex = 0;
    this.reportPanelOpen = false;
    this.collapsedSections = {};
    this.resetZoom();
    this.visibleChange.emit(false);
  }

  close(): void {
    this.onDialogHide();
  }

  // ── Analysis ───────────────────────────────────────────────────────────

  runAnalysis(): void {
    if (!this.aiEnabled) {
      this.toast.info('AI is off', 'Turn on AI analysis to generate a clinical interpretation.');
      return;
    }

    const beforeIds = this.beforePhotos.map((file) => Number(file?.id)).filter((id) => id > 0);
    const afterIds = this.afterPhotos.map((file) => Number(file?.id)).filter((id) => id > 0);
    if (!beforeIds.length || !afterIds.length) {
      this.toast.warn('Missing photos', 'Pick at least one before photo and one after photo.');
      return;
    }

    this.cancelAnalysis(true);
    this.cancelled = false;
    this.loading = true;
    this.error = null;
    this.elapsedSeconds = 0;
    this.startElapsedTimer();
    this.cdr.markForCheck();

    this.timeoutTimer = setTimeout(() => this.handleTimeout(), this.timeoutMs);

    const prescriptionId = Number(this.prescriptionId);
    this.requestSub = this.reportAnalysisService
      .analyzePhotoProgress({
        before_report_ids: beforeIds,
        after_report_ids: afterIds,
        prescription_id: prescriptionId > 0 ? prescriptionId : null,
        chief_complaints: this.chiefComplaints,
        diagnoses: this.diagnoses,
        treatment_context: this.treatmentContext,
      })
      .pipe(
        finalize(() => {
          this.clearTimers();
          this.loading = false;
          this.requestSub = undefined;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            const message = res?.message || 'Unable to compare these photos.';
            this.error = message;
            this.toast.warn('Analysis unavailable', message);
            return;
          }
          this.analysis = res.data;
          this.error = null;
          this.rebuildViewModel();
          this.reportPanelOpen = true;
          this.analysisSaved.emit(res.data);
          this.toast.add({
            severity: 'success',
            summary: 'Photo progress ready',
            detail: 'Clinical interpretation is ready in the report panel.',
          });
        },
        error: (err) => {
          if (this.cancelled) return;
          const message = err?.error?.message || err?.message || 'Unable to compare these photos right now.';
          this.error = message;
          this.toast.warn('Analysis unavailable', message);
        },
      });
  }

  cancelAnalysis(silent = true): void {
    this.cancelled = silent;
    this.clearTimers();
    this.requestSub?.unsubscribe();
    this.requestSub = undefined;
    this.loading = false;
    this.cdr.markForCheck();
  }

  // ── PDF export (programmatic A4 document — not a UI screenshot) ─────────

  async exportPdf(): Promise<void> {
    if (!this.viewModel) return;

    this.exporting = true;
    this.cdr.markForCheck();

    try {
      const vm = this.viewModel;
      const pairs = vm.pairs.length
        ? vm.pairs
        : this.activePair
          ? [this.activePair]
          : [];

      const [pairImages, logoDataUrl] = await Promise.all([
        Promise.all(
          pairs.map(async (pair) => ({
            pair,
            beforeImg: await this.loadImageDataUrl(pair.beforePhoto?.url || ''),
            afterImg: await this.loadImageDataUrl(pair.afterPhoto?.url || ''),
          })),
        ),
        this.loadImageDataUrl('/clinic/logo.png'),
      ]);

      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      this.buildPhotoProgressPdf(doc, vm, pairImages, logoDataUrl);

      const patientName = this.patientExportLabel.split('·')[0]?.trim() || 'patient';
      doc.save(`photo-progress-${patientName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } catch (error) {
      console.error(error);
      this.toast.error('Export failed', 'Unable to create the progress PDF.');
    } finally {
      this.exporting = false;
      this.cdr.markForCheck();
    }
  }

  // ── Template helpers ───────────────────────────────────────────────────

  getCaption(file: ComparePhoto, side: 'before' | 'after'): string {
    const role = side === 'before' ? 'Before' : 'After';
    return `${role} · ${this.formatDate(file?.report_date || file?.uploaded_at || '')}`;
  }

  trackByPhotoId(_index: number, file: ComparePhoto): number | string {
    return file?.id ?? file?.url ?? _index;
  }

  trackByPairIndex(_index: number, pair: PairedFinding): number {
    return pair.index;
  }

  toneWord(tone: ClinicalTone): string {
    switch (tone) {
      case 'positive':
        return 'Improving';
      case 'negative':
        return 'Worsening';
      case 'warning':
        return 'Concerning';
      case 'caution':
        return 'Mixed';
      default:
        return 'Stable';
    }
  }

  toneIcon(tone: ClinicalTone): string {
    switch (tone) {
      case 'positive':
        return 'pi-arrow-up-right';
      case 'negative':
        return 'pi-arrow-down-right';
      case 'warning':
        return 'pi-exclamation-triangle';
      case 'caution':
        return 'pi-minus';
      default:
        return 'pi-minus';
    }
  }

  shortMetricText(value: string, max = 42): string {
    const text = String(value || '').trim();
    if (!text) return '—';
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1).trim()}…`;
  }


  // ── Private ────────────────────────────────────────────────────────────

  private buildPhotoProgressPdf(
    doc: jsPDF,
    vm: PhotoProgressViewModel,
    pairImages: Array<{ pair: PairedFinding; beforeImg: string | null; afterImg: string | null }>,
    logoDataUrl: string | null = null,
  ): void {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const mx = 14;
    const cw = pageW - mx * 2;
    const footerY = pageH - 12;
    const bottomSafe = footerY - 4;
    let y = 0;
    let pageNum = 1;

    const brandPink: [number, number, number] = [227, 28, 121];
    const brandOrange: [number, number, number] = [246, 137, 32];
    const ink: [number, number, number] = [15, 23, 42];
    const muted: [number, number, number] = [100, 116, 139];
    const line: [number, number, number] = [226, 232, 240];
    const surface: [number, number, number] = [248, 250, 252];
    const white: [number, number, number] = [255, 255, 255];
    const beforeBlue: [number, number, number] = [3, 105, 161];
    const afterOrange: [number, number, number] = [234, 88, 12];

    const patientName = this.patientExportLabel.split('·')[0]?.trim() || this.patientExportLabel;
    const siteLabel = (vm.site || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const outcomeLabel = (vm.levelLabel || 'Assessment').replace(/\b\w/g, (c) => c.toUpperCase());
    const toneRgb = this.toneRgb(vm.levelTone);
    const toneBg = this.toneBg(vm.levelTone);
    const pairs = pairImages.map((i) => i.pair);
    const beforeCount = Math.max(pairs.length, this.beforePhotos.length);
    const afterCount = Math.max(pairs.length, this.afterPhotos.length);
    const firstBeforeDate = pairs.find((p) => p.beforeDate)?.beforeDate || '';
    const lastAfterDate = [...pairs].reverse().find((p) => p.afterDate)?.afterDate || '';
    const dateSpan =
      firstBeforeDate && lastAfterDate
        ? `${firstBeforeDate}  →  ${lastAfterDate}`
        : firstBeforeDate || lastAfterDate || '—';
    const generatedOn = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    doc.setProperties({
      title: `Photo Progress — ${patientName}`,
      subject: vm.title || siteLabel || 'Clinical photo progress',
      author: 'Dr Sancheti Clinic',
      creator: 'Dr Sancheti Clinic EMR',
    });

    const rule = (
      x1: number,
      ry: number,
      x2: number,
      color: [number, number, number] = line,
      w = 0.35,
    ) => {
      doc.setDrawColor(...color);
      doc.setLineWidth(w);
      doc.line(x1, ry, x2, ry);
    };

    const sectionTitle = (label: string) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...ink);
      doc.text(label.toUpperCase(), mx, y + 4);
      y += 5;
      rule(mx, y, mx + cw, brandPink, 0.9);
      y += 4;
    };

    const newPage = (banner = 'Photo Progress Report') => {
      doc.addPage();
      pageNum += 1;
      doc.setFillColor(...brandPink);
      doc.rect(0, 0, pageW / 2, 7, 'F');
      doc.setFillColor(...brandOrange);
      doc.rect(pageW / 2, 0, pageW / 2, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...white);
      doc.text(banner, mx, 4.8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(patientName, pageW - mx, 4.8, { align: 'right' });
      y = 12;
    };

    const ensureSpace = (needed: number, banner?: string) => {
      if (y + needed <= bottomSafe) return;
      newPage(banner);
    };

    const drawImageFit = (
      dataUrl: string | null,
      x: number,
      fy: number,
      bw: number,
      bh: number,
    ) => {
      doc.setFillColor(...surface);
      doc.rect(x, fy, bw, bh, 'F');
      if (!dataUrl) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text('Photo unavailable', x + bw / 2, fy + bh / 2, { align: 'center' });
        return;
      }
      try {
        const props = doc.getImageProperties(dataUrl);
        const ratio = Math.min(bw / props.width, bh / props.height);
        const dw = props.width * ratio;
        const dh = props.height * ratio;
        doc.addImage(
          dataUrl,
          dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG',
          x + (bw - dw) / 2,
          fy + (bh - dh) / 2,
          dw,
          dh,
        );
      } catch {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text('Photo unavailable', x + bw / 2, fy + bh / 2, { align: 'center' });
      }
    };

    const drawLetterhead = () => {
      if (logoDataUrl) {
        try {
          const props = doc.getImageProperties(logoDataUrl);
          const lh = 18;
          const lw = Math.min(46, props.width * (lh / props.height));
          doc.addImage(
            logoDataUrl,
            logoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG',
            mx,
            8,
            lw,
            lh,
          );
        } catch { /* ignore */ }
      }

      const tx = mx + (logoDataUrl ? 50 : 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(...ink);
      doc.text('Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,', tx, 11);
      doc.text('Maharshi Nagar, Pune, Maharashtra – 411037', tx, 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...muted);
      doc.text('Ph: 8070 7080 01 / 02 / 03  ·  drshaileshsancheti@gmail.com', tx, 19);

      y = 26;
      doc.setLineWidth(1.8);
      doc.setDrawColor(...brandPink);
      doc.line(mx, y, pageW / 2, y);
      doc.setDrawColor(...brandOrange);
      doc.line(pageW / 2, y, pageW - mx, y);
      y += 2;

      const half = cw / 2;
      doc.setFillColor(...brandPink);
      doc.rect(mx, y, half, 8, 'F');
      doc.setFillColor(...brandOrange);
      doc.rect(mx + half, y, half, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...white);
      doc.text('SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES', mx + 3, y + 5.2);
      doc.text('Morning 11–2 pm  ·  Evening 6–8 pm', pageW - mx - 3, y + 5.2, { align: 'right' });
      y += 12;
    };

    const drawPatientStrip = () => {
      const boxH = 18;
      doc.setFillColor(...surface);
      doc.setDrawColor(...line);
      doc.setLineWidth(0.3);
      doc.roundedRect(mx, y, cw, boxH, 2, 2, 'FD');

      const col2 = mx + cw / 2 + 2;
      const row = (label: string, value: string, x: number, ry: number) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(...muted);
        doc.text(label.toUpperCase(), x, ry);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...ink);
        const clipped = (doc.splitTextToSize(value || '—', cw / 2 - 8) as string[])[0];
        doc.text(clipped, x, ry + 4.5);
      };

      row('Patient', this.patientExportLabel, mx + 5, y + 4);
      row('Site', siteLabel || '—', col2, y + 4);
      row('Interval', dateSpan, mx + 5, y + 11);
      row('Photos', `${beforeCount} before  ·  ${afterCount} after`, col2, y + 11);
      y += boxH + 6;
    };

    const drawOutcomeHero = () => {
      const heroH = 22;
      doc.setFillColor(...toneBg);
      doc.setDrawColor(...toneRgb);
      doc.setLineWidth(0.5);
      doc.roundedRect(mx, y, cw, heroH, 2.5, 2.5, 'FD');
      doc.setFillColor(...toneRgb);
      doc.roundedRect(mx, y, 5, heroH, 1.5, 1.5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...toneRgb);
      doc.text('OVERALL CLINICAL OUTCOME', mx + 9, y + 6);
      doc.setFontSize(16);
      doc.setTextColor(...ink);
      doc.text(outcomeLabel, mx + 9, y + 15);

      const chips: Array<{ count: number; label: string; tone: ClinicalTone }> = [
        { count: vm.metricSummary.positive, label: 'Improving', tone: 'positive' },
        { count: vm.metricSummary.caution, label: 'Mixed', tone: 'caution' },
        { count: vm.metricSummary.warning, label: 'Concerning', tone: 'warning' },
        { count: vm.metricSummary.negative, label: 'Worsening', tone: 'negative' },
        { count: vm.metricSummary.neutral, label: 'Stable', tone: 'neutral' },
      ].filter((c) => c.count > 0) as Array<{ count: number; label: string; tone: ClinicalTone }>;

      let chipX = mx + cw - 4;
      for (const chip of [...chips].reverse()) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        const text = `${chip.count} ${chip.label}`;
        const w = Math.max(22, doc.getTextWidth(text) + 8);
        chipX -= w + 2;
        doc.setFillColor(...this.toneRgb(chip.tone));
        doc.roundedRect(chipX, y + 7, w, 8, 2, 2, 'F');
        doc.setTextColor(...white);
        doc.text(text, chipX + w / 2, y + 12.5, { align: 'center' });
      }
      y += heroH + 7;
    };

    const drawMetricCards = () => {
      if (!vm.metrics.length) return;

      sectionTitle('Change at a Glance');
      const gap = 4;
      const cardW = (cw - gap) / 2;
      const cardPad = 4;
      let col = 0;
      let rowY = y;

      vm.metrics.forEach((m, idx) => {
        const before = m.beforeText || (m.afterText ? '—' : m.text) || '—';
        const after = m.afterText || (m.beforeText ? '—' : '') || '—';
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const beforeLines = doc.splitTextToSize(before, cardW - cardPad * 2 - 2) as string[];
        const afterLines = after ? (doc.splitTextToSize(after, cardW - cardPad * 2 - 2) as string[]) : [];
        const bodyLines = Math.max(beforeLines.length, afterLines.length, 1);
        const cardH = 16 + bodyLines * 3.6;

        if (col === 0) {
          ensureSpace(cardH + 4, 'Clinical Metrics');
          rowY = y;
        }

        const x = mx + col * (cardW + gap);
        const tone = this.toneRgb(m.tone);

        doc.setFillColor(...white);
        doc.setDrawColor(...line);
        doc.setLineWidth(0.25);
        doc.roundedRect(x, rowY, cardW, cardH, 2, 2, 'FD');
        doc.setFillColor(...tone);
        doc.rect(x, rowY, 2.5, cardH, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...ink);
        doc.text(m.label, x + cardPad + 1, rowY + 5.5);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        const trend = this.toneWord(m.tone).toUpperCase();
        const pillW = Math.max(18, doc.getTextWidth(trend) + 6);
        doc.setFillColor(...tone);
        doc.roundedRect(x + cardW - pillW - cardPad, rowY + 2.5, pillW, 5.5, 1.5, 1.5, 'F');
        doc.setTextColor(...white);
        doc.text(trend, x + cardW - pillW / 2 - cardPad, rowY + 6.2, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...beforeBlue);
        doc.text('BEFORE', x + cardPad + 1, rowY + 10.5);
        doc.setTextColor(...afterOrange);
        doc.text('AFTER', x + cardW / 2 + 1, rowY + 10.5);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(51, 65, 85);
        let ty = rowY + 14;
        for (const row of beforeLines) {
          doc.text(row, x + cardPad + 1, ty);
          ty += 3.6;
        }
        ty = rowY + 14;
        for (const row of afterLines) {
          doc.text(row, x + cardW / 2 + 1, ty);
          ty += 3.6;
        }

        col += 1;
        if (col === 2) {
          col = 0;
          y = rowY + cardH + gap;
        } else if (idx === vm.metrics.length - 1) {
          y = rowY + cardH + 4;
        }
      });
      y += 2;
    };

    const drawPhotoComparison = (
      item: { pair: PairedFinding; beforeImg: string | null; afterImg: string | null },
      idx: number,
      total: number,
      photoH: number,
    ) => {
      const pair = item.pair;
      const gap = 4;
      const photoW = (cw - gap) / 2;

      ensureSpace(photoH + 28, total > 1 ? `Clinical Photos (${idx + 1}/${total})` : 'Clinical Photos');

      if (total > 1) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...ink);
        doc.text(`Comparison ${idx + 1} of ${total}`, mx, y + 3);
        if (pair.beforeDate || pair.afterDate) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...muted);
          doc.text(
            [pair.beforeDate, pair.afterDate].filter(Boolean).join('  →  '),
            pageW - mx,
            y + 3,
            { align: 'right' },
          );
        }
        y += 6;
      }

      const drawSlot = (
        x: number,
        label: string,
        date: string,
        dataUrl: string | null,
        accent: [number, number, number],
      ) => {
        doc.setDrawColor(...accent);
        doc.setLineWidth(0.7);
        doc.roundedRect(x, y, photoW, photoH, 2, 2, 'S');
        drawImageFit(dataUrl, x + 1, y + 1, photoW - 2, photoH - 2);

        doc.setFillColor(...accent);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        const lbl = label.toUpperCase();
        const lw = Math.max(20, doc.getTextWidth(lbl) + 8);
        doc.roundedRect(x + 3, y + 3, lw, 6.5, 1.5, 1.5, 'F');
        doc.setTextColor(...white);
        doc.text(lbl, x + 3 + lw / 2, y + 7.5, { align: 'center' });

        if (date) {
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(...line);
          doc.setLineWidth(0.25);
          const dw = doc.getTextWidth(date) + 6;
          doc.roundedRect(x + photoW - dw - 3, y + photoH - 8, dw, 5.5, 1, 1, 'FD');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
          doc.setTextColor(...ink);
          doc.text(date, x + photoW - dw / 2 - 3, y + photoH - 4.5, { align: 'center' });
        }
      };

      drawSlot(mx, 'Before', pair.beforeDate || '', item.beforeImg, beforeBlue);
      drawSlot(mx + photoW + gap, 'After', pair.afterDate || '', item.afterImg, afterOrange);

      const midX = mx + photoW + gap / 2;
      const midY = y + photoH / 2;
      doc.setFillColor(...ink);
      doc.circle(midX, midY, 4.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...white);
      doc.text('→', midX, midY + 1.2, { align: 'center' });

      y += photoH + 5;

      if (pair.beforeFindings || pair.afterFindings) {
        const findGap = 4;
        const both = !!(pair.beforeFindings && pair.afterFindings);
        const findW = both ? (cw - findGap) / 2 : cw;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const beforeRows = pair.beforeFindings
          ? (doc.splitTextToSize(pair.beforeFindings, findW - 8) as string[])
          : [];
        const afterRows = pair.afterFindings
          ? (doc.splitTextToSize(pair.afterFindings, findW - 8) as string[])
          : [];
        const findH = Math.max(beforeRows.length, afterRows.length, 1) * 3.8 + 12;
        ensureSpace(findH + 2);

        const drawFinding = (
          x: number,
          title: string,
          rows: string[],
          accent: [number, number, number],
          bg: [number, number, number],
        ) => {
          doc.setFillColor(...bg);
          doc.setDrawColor(...accent);
          doc.setLineWidth(0.35);
          doc.roundedRect(x, y, findW, findH, 2, 2, 'FD');
          doc.setFillColor(...accent);
          doc.rect(x, y, 2, findH, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(...accent);
          doc.text(title.toUpperCase(), x + 5, y + 5.5);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(...ink);
          let ty = y + 10;
          for (const row of rows) {
            doc.text(row, x + 5, ty);
            ty += 3.8;
          }
        };

        if (both) {
          drawFinding(mx, 'Before findings', beforeRows, beforeBlue, [240, 249, 255]);
          drawFinding(mx + findW + findGap, 'After findings', afterRows, afterOrange, [255, 247, 237]);
        } else if (pair.beforeFindings) {
          drawFinding(mx, 'Before findings', beforeRows, beforeBlue, [240, 249, 255]);
        } else {
          drawFinding(mx, 'After findings', afterRows, afterOrange, [255, 247, 237]);
        }
        y += findH + 4;
      }
    };

    const drawTextSection = (title: string, text: string, shaded = false) => {
      if (!text?.trim()) return;
      sectionTitle(title);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      const rows = doc.splitTextToSize(text.trim(), cw - 8) as string[];
      const lh = 4.2;
      const boxH = rows.length * lh + 8;
      ensureSpace(boxH + 2, 'Clinical Summary');
      if (shaded) {
        doc.setFillColor(...surface);
        doc.setDrawColor(...line);
        doc.setLineWidth(0.25);
        doc.roundedRect(mx, y, cw, boxH, 2, 2, 'FD');
      }
      doc.setTextColor(...ink);
      let ty = y + (shaded ? 6 : 0);
      for (const row of rows) {
        doc.text(row, mx + 4, ty);
        ty += lh;
      }
      y += boxH + 4;
    };

    const drawCaveats = () => {
      if (!vm.caveats.length) return;
      sectionTitle('Comparability Notes');
      const lines: string[] = [];
      for (const c of vm.caveats) {
        lines.push(...(doc.splitTextToSize(`•  ${c}`, cw - 10) as string[]));
      }
      const boxH = lines.length * 3.8 + 8;
      ensureSpace(boxH + 2);
      doc.setFillColor(254, 252, 232);
      doc.setDrawColor(234, 179, 8);
      doc.setLineWidth(0.35);
      doc.roundedRect(mx, y, cw, boxH, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(120, 90, 10);
      let cy = y + 6;
      for (const row of lines) {
        doc.text(row, mx + 4, cy);
        cy += 3.8;
      }
      y += boxH + 4;
    };

    const drawSignature = () => {
      ensureSpace(28);
      const sigX = mx + cw - 62;
      const sigY = Math.max(y + 8, bottomSafe - 24);
      rule(sigX, sigY, mx + cw, ink, 0.6);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...ink);
      doc.text('Dr. Shailesh Sancheti', sigX, sigY + 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...muted);
      doc.text('MD (Homeopathy)', sigX, sigY + 11);
      y = sigY + 16;
    };

    const stampFooters = () => {
      const total = doc.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        rule(mx, footerY - 2, pageW - mx, line, 0.4);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...muted);
        doc.text('Dr Sancheti Clinic  ·  Photo Progress Report', mx, footerY + 2);
        doc.text(`Confidential — ${patientName}`, pageW / 2, footerY + 2, { align: 'center' });
        doc.text(`Page ${i} of ${total}`, pageW - mx, footerY + 2, { align: 'right' });
      }
    };

    // ═══ PAGE 1 — Letterhead + summary ═══
    drawLetterhead();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...ink);
    doc.text('PHOTO PROGRESS REPORT', pageW / 2, y, { align: 'center' });
    y += 5;
    if (siteLabel) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...muted);
      doc.text(siteLabel, pageW / 2, y, { align: 'center' });
      y += 4;
    }
    doc.setFontSize(8);
    doc.text(`Generated ${generatedOn}`, pageW / 2, y, { align: 'center' });
    y += 7;

    drawPatientStrip();
    drawOutcomeHero();
    drawMetricCards();

    // Single pair: photos on page 1 when space allows
    if (pairImages.length === 1) {
      const remaining = bottomSafe - y;
      const photoH = Math.min(88, Math.max(58, remaining - 36));
      if (photoH >= 58) {
        sectionTitle('Clinical Photos');
        drawPhotoComparison(pairImages[0], 0, 1, photoH);
      } else {
        newPage('Clinical Photos');
        drawPhotoComparison(pairImages[0], 0, 1, 92);
      }
    }

    // Multiple pairs: dedicated photo pages
    if (pairImages.length > 1) {
      pairImages.forEach((item, idx) => {
        newPage(`Clinical Photos (${idx + 1}/${pairImages.length})`);
        drawPhotoComparison(item, idx, pairImages.length, 98);
      });
    }

    // Care & notes page
    const hasCare =
      vm.intervalComparison ||
      vm.recommendedCare ||
      vm.clinicianNotes ||
      vm.caveats.length;
    if (hasCare) {
      newPage('Clinical Summary & Care Plan');
      drawTextSection('Clinical Summary', vm.intervalComparison, true);

      if (vm.recommendedCare || vm.clinicianNotes) {
        sectionTitle('Care & Notes');
        const gap = 5;
        const colW = (cw - gap) / 2;
        const careRows = vm.recommendedCare
          ? (doc.splitTextToSize(vm.recommendedCare, colW - 8) as string[])
          : [];
        const noteRows = vm.clinicianNotes
          ? (doc.splitTextToSize(vm.clinicianNotes, colW - 8) as string[])
          : [];
        const boxH = Math.max(careRows.length, noteRows.length, 1) * 4 + 14;
        ensureSpace(boxH + 2);

        const drawCareBox = (
          x: number,
          title: string,
          rows: string[],
          accent: [number, number, number],
        ) => {
          if (!rows.length) return;
          doc.setFillColor(...white);
          doc.setDrawColor(...line);
          doc.setLineWidth(0.25);
          doc.roundedRect(x, y, colW, boxH, 2, 2, 'FD');
          doc.setFillColor(...accent);
          doc.rect(x, y, 2, boxH, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...accent);
          doc.text(title.toUpperCase(), x + 5, y + 6);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(...ink);
          let ty = y + 11;
          for (const row of rows) {
            doc.text(row, x + 5, ty);
            ty += 4;
          }
        };

        if (vm.recommendedCare && vm.clinicianNotes) {
          drawCareBox(mx, 'Recommended care', careRows, brandPink);
          drawCareBox(mx + colW + gap, 'Clinician notes', noteRows, beforeBlue);
        } else if (vm.recommendedCare) {
          drawCareBox(mx, 'Recommended care', careRows, brandPink);
        } else {
          drawCareBox(mx, 'Clinician notes', noteRows, beforeBlue);
        }
        y += boxH + 4;
      }

      drawCaveats();
      drawSignature();
    }

    stampFooters();
  }

  private toneRgb(tone: ClinicalTone): [number, number, number] {
    switch (tone) {
      case 'positive': return [5, 150, 105];
      case 'caution': return [202, 138, 4];
      case 'warning': return [234, 88, 12];
      case 'negative': return [220, 38, 38];
      default: return [71, 85, 105];
    }
  }

  private toneBg(tone: ClinicalTone): [number, number, number] {
    switch (tone) {
      case 'positive': return [236, 253, 245];
      case 'caution': return [254, 252, 232];
      case 'warning': return [255, 247, 237];
      case 'negative': return [254, 242, 242];
      default: return [248, 250, 252];
    }
  }

  private async loadImageDataUrl(url: string): Promise<string | null> {
    const src = String(url || '').trim();
    if (!src) return null;

    // Prefer fetch so we can attach CORS and convert to a stable data URL
    try {
      const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (res.ok) {
        const blob = await res.blob();
        const dataUrl = await new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
        if (dataUrl) return dataUrl;
      }
    } catch {
      // fall through to Image element
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx || !canvas.width || !canvas.height) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  private removePanListeners(): void {
    if (this.boundPanMove) {
      document.removeEventListener('mousemove', this.boundPanMove);
      this.boundPanMove = null;
    }
    if (this.boundPanEnd) {
      document.removeEventListener('mouseup', this.boundPanEnd);
      this.boundPanEnd = null;
    }
  }

  private handleTimeout(): void {
    const message = 'Analysis took too long. Try again with fewer photos, or check your connection.';
    this.error = message;
    this.cancelled = true;
    this.requestSub?.unsubscribe();
    this.requestSub = undefined;
    this.loading = false;
    this.clearTimers();
    this.toast.warn('Analysis unavailable', message);
    this.cdr.markForCheck();
  }

  private startElapsedTimer(): void {
    this.elapsedTimer = setInterval(() => {
      this.elapsedSeconds += 1;
      this.cdr.markForCheck();
    }, 1000);
  }

  private clearTimers(): void {
    if (this.timeoutTimer !== null) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.elapsedTimer !== null) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private tryLoadExisting(): void {
    const newest = this.afterPhotos[this.afterPhotos.length - 1];
    const newestId = Number(newest?.id);
    if (!newestId || !this.aiEnabled) {
      this.analysis = null;
      this.viewModel = null;
      this.cdr.markForCheck();
      return;
    }

    const beforeIds = this.beforePhotos.map((file) => Number(file?.id)).filter((id) => id > 0);
    const afterIds = this.afterPhotos.map((file) => Number(file?.id)).filter((id) => id > 0);

    this.reportAnalysisService.listAnalyses(newestId).subscribe({
      next: (res: any) => {
        const analyses = Array.isArray(res?.data) ? res.data : [];
        const match = analyses.find((item: any) => {
          if (item?.analysis_type !== 'photo_progress') return false;
          const savedBefore = (item?.comparison_diff?.before_report_ids || []).map((id: any) => Number(id));
          const savedAfter = (item?.comparison_diff?.after_report_ids || []).map((id: any) => Number(id));
          if (savedBefore.length !== beforeIds.length || savedAfter.length !== afterIds.length) return false;
          return beforeIds.every((id) => savedBefore.includes(id)) && afterIds.every((id) => savedAfter.includes(id));
        });
        this.analysis = match || null;
        this.rebuildViewModel();
        if (this.analysis) {
          this.reportPanelOpen = true;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.analysis = null;
        this.viewModel = null;
        this.cdr.markForCheck();
      },
    });
  }

  private rebuildViewModel(): void {
    if (!this.analysis) {
      this.viewModel = null;
      return;
    }

    this.resetZoom();
    this.collapsedSections = {};

    const diff = this.analysis?.comparison_diff || {};
    const raw = this.analysis?.raw_response || {};
    const level = String(diff.overall_level || raw.overall_level || '');
    const levelTone = this.levelTone(level);
    const observations = this.readObservations(diff, raw);
    const observationsBefore = observations.filter((obs) => this.observationGroup(obs) === 'before');
    const observationsAfter = observations.filter((obs) => this.observationGroup(obs) === 'after');
    const pairing = this.buildPairs(observationsBefore, observationsAfter);

    const metrics = this.buildMetrics(diff, raw, levelTone);
    this.viewModel = {
      title: diff.title || raw.title || 'Clinical photo progress',
      site: diff.anatomical_site || raw.anatomical_site || '',
      level,
      levelTone,
      levelIcon: this.levelIcon(levelTone),
      levelLabel: level ? level.replace(/_/g, ' ') : '',
      intervalComparison:
        diff.interval_comparison || raw.interval_comparison || this.analysis?.summary_text || '',
      recommendedCare: diff.recommended_care || raw.recommended_care || '',
      clinicianNotes: diff.clinician_notes || raw.clinician_notes || '',
      metrics,
      pairs: pairing.pairs,
      unpairedBefore: pairing.unpairedBefore,
      unpairedAfter: pairing.unpairedAfter,
      caveats: this.readCaveats(diff, raw),
      metricSummary: this.summarizeMetrics(metrics),
    };
    this.activePairIndex = 0;
    this.reportPanelOpen = true;
  }

  private buildPairs(
    observationsBefore: PhotoObservation[],
    observationsAfter: PhotoObservation[],
  ): {
    pairs: PairedFinding[];
    unpairedBefore: Array<{ photo: ComparePhoto | null; findings: string; date: string }>;
    unpairedAfter: Array<{ photo: ComparePhoto | null; findings: string; date: string }>;
  } {
    const pairCount = Math.max(
      this.beforePhotos.length,
      this.afterPhotos.length,
      observationsBefore.length,
      observationsAfter.length,
    );
    const pairs: PairedFinding[] = [];

    for (let i = 0; i < pairCount; i++) {
      const beforePhoto = this.beforePhotos[i] || null;
      const afterPhoto = this.afterPhotos[i] || null;
      const beforeObs = observationsBefore[i];
      const afterObs = observationsAfter[i];
      if (!beforePhoto && !afterPhoto && !beforeObs && !afterObs) continue;

      pairs.push({
        index: i,
        beforePhoto,
        afterPhoto,
        beforeFindings: beforeObs?.visible_findings || '',
        afterFindings: afterObs?.visible_findings || '',
        beforeDate:
          beforeObs?.date ||
          this.formatDate(beforePhoto?.report_date || beforePhoto?.uploaded_at || ''),
        afterDate:
          afterObs?.date ||
          this.formatDate(afterPhoto?.report_date || afterPhoto?.uploaded_at || ''),
      });
    }

    return { pairs, unpairedBefore: [], unpairedAfter: [] };
  }

  private buildMetrics(diff: any, raw: any, levelTone: ClinicalTone): PhotoProgressMetric[] {
    const defs = [
      { key: 'skin_color', label: 'Skin color', icon: 'pi-palette' },
      { key: 'texture', label: 'Texture', icon: 'pi-th-large' },
      { key: 'patch_size', label: 'Patch size', icon: 'pi-chart-pie' },
      { key: 'hair', label: 'Hair', icon: 'pi-sparkles' },
      { key: 'overall_extent', label: 'Overall extent', icon: 'pi-expand' },
    ];

    return defs
      .map((def) => {
        const text = String(diff.metrics?.[def.key] || raw[def.key] || '');
        const split = this.splitBeforeAfter(text);
        return { ...def, text, beforeText: split.before, afterText: split.after, tone: this.inferTone(text, levelTone) };
      })
      .filter((item) => !!String(item.text || '').trim());
  }

  private summarizeMetrics(metrics: PhotoProgressMetric[]): PhotoProgressViewModel['metricSummary'] {
    const summary = { positive: 0, caution: 0, warning: 0, negative: 0, neutral: 0 };
    for (const metric of metrics) {
      summary[metric.tone] += 1;
    }
    return summary;
  }

  private splitBeforeAfter(text: string): { before: string; after: string } {
    const value = String(text || '').trim();
    if (!value) return { before: '', after: '' };
    const match =
      value.match(/before\s*[:\-]?\s*(.+?)\s*[;|·]\s*after\s*[:\-]?\s*(.+)$/i) ||
      value.match(/before\s*[:\-]?\s*(.+?)\s+after\s*[:\-]?\s*(.+)$/i);
    if (match) return { before: match[1].trim(), after: match[2].trim() };
    return { before: '', after: '' };
  }

  private readObservations(diff: any, raw: any): PhotoObservation[] {
    const fromDiff = diff.photo_observations;
    const fromRaw = raw.photo_observations;
    const rows = Array.isArray(fromDiff) && fromDiff.length ? fromDiff : fromRaw;
    return Array.isArray(rows) ? rows : [];
  }

  private readCaveats(diff: any, raw: any): string[] {
    const fromDiff = diff.caveats;
    const fromRaw = raw.caveats;
    const rows = Array.isArray(fromDiff) && fromDiff.length ? fromDiff : fromRaw;
    return Array.isArray(rows) ? rows.filter((item: unknown) => String(item || '').trim()) : [];
  }

  private observationGroup(obs: PhotoObservation): 'before' | 'after' | '' {
    const value = String(obs?.group || obs?.label || '').toLowerCase();
    if (value === 'before' || value === 'after') return value;
    return '';
  }

  private levelTone(level: string): ClinicalTone {
    switch (level.toLowerCase()) {
      case 'improved': return 'positive';
      case 'partial': return 'warning';
      case 'worsened': return 'negative';
      case 'unchanged':
      case 'stable': return 'neutral';
      default: return 'neutral';
    }
  }

  private levelIcon(tone: ClinicalTone): string {
    switch (tone) {
      case 'positive': return 'pi-check-circle';
      case 'warning': return 'pi-exclamation-circle';
      case 'caution': return 'pi-exclamation-triangle';
      case 'negative': return 'pi-times-circle';
      default: return 'pi-minus-circle';
    }
  }

  private inferTone(text: string, fallback: ClinicalTone): ClinicalTone {
    const value = text.toLowerCase();
    if (!value.trim()) return 'neutral';
    const neg = (value.match(/\b(worsen(?:ed|ing)?|increased|darker|thicker|more extensive|spread|heavier|more lesions|deteriorat)\b/g) || []).length;
    const pos = (value.match(/\b(improv(?:ed|ement)|reduc(?:ed|tion)|lighter|smoother|narrow(?:ed|ing)|heal(?:ed|ing)|less|fewer|clearer|healthier|faded)\b/g) || []).length;
    if (pos > neg + 1) return 'positive';
    if (neg > pos + 1) return 'negative';
    if (pos && neg) return 'caution';
    if (neg > pos) return 'warning';
    if (pos > neg) return 'positive';
    if (/\b(stable|unchanged|same|no (?:significant )?change)\b/.test(value)) return 'neutral';
    return fallback === 'neutral' ? 'neutral' : fallback;
  }

  private formatDate(value: string): string {
    if (!value) return 'Date unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
